import { Field } from "@base-ui/react/field";
import type { ReactNode } from "react";
import { cx } from "../../lib/cx";

/**
 * The label, description and error around one input, on Base UI's Field.
 * Shared by `TextField`, `TextArea` and `NumberField`; not exported, because
 * a screen that wants a label uses one of those.
 *
 * Validation is the caller's (plan D09: the contract Zod schema on save), so
 * `error` is a finished string. Field wires the label to the control and the
 * description and error to `aria-describedby`; `invalid` marks the control
 * `aria-invalid` and gives it `data-invalid`, which the inputs paint.
 */

export interface FieldFrameProps {
  children: ReactNode;
  className?: string;
  description?: string;
  disabled?: boolean;
  error?: string;
  label: string;
}

export const FIELD_INPUT =
  "w-full rounded-md bg-surface px-4 text-base text-ink ring-1 ring-line-strong transition-shadow placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-accent data-disabled:cursor-not-allowed data-disabled:opacity-40 data-invalid:ring-2 data-invalid:ring-danger";

export function FieldFrame({
  label,
  description,
  error,
  disabled,
  className,
  children,
}: FieldFrameProps) {
  return (
    <Field.Root
      className={cx("flex flex-col gap-1.5", className)}
      disabled={disabled}
      invalid={Boolean(error)}
    >
      <Field.Label className="font-semibold text-base text-ink">
        {label}
      </Field.Label>
      {children}
      {description ? (
        <Field.Description className="text-ink-3 text-sm leading-snug">
          {description}
        </Field.Description>
      ) : null}
      {error ? (
        <Field.Error className="text-danger text-sm leading-snug" match>
          {error}
        </Field.Error>
      ) : null}
    </Field.Root>
  );
}
